import { createBrowserRouter } from "react-router";
import { RootLayout } from "./components/RootLayout";
import { HomePage } from "./components/HomePage";
import { LibraryPage } from "./components/LibraryPage";
import { NotFoundPage } from "./components/NotFoundPage";
import { OnboardingGate } from "./components/OnboardingGate";
import { CapturePage } from "./components/CapturePage";
import { RouteErrorBoundary } from "./components/RouteErrorBoundary";
import { isChunkLoadError, recoverFromChunkError } from "./lib/chunkRecovery";

// Heavier, less-visited routes are code-split so phones don't pay for the
// whole app (one ~1.2 MB bundle) just to reach the home screen.
//
// The cost of splitting is that a deploy retires the hashed chunk this build
// points at, and every navigation in an already-open tab then 404s. Reload onto
// the current build once when that happens; if recovery declines (we've already
// tried this session) the error falls through to RouteErrorBoundary.
const lazyPage = (loader: () => Promise<Record<string, any>>, name: string) =>
  async () => {
    try {
      return { Component: (await loader())[name] };
    } catch (error) {
      if (isChunkLoadError(error) && (await recoverFromChunkError())) {
        // The reload is in flight. Never resolve, so the router keeps showing
        // the pending state instead of flashing an error on the way out.
        await new Promise(() => {});
      }
      throw error;
    }
  };

export const router = createBrowserRouter([
  {
    path: "/",
    Component: RootLayout,
    ErrorBoundary: RouteErrorBoundary,
    children: [
      { index: true, Component: HomePage },
      { path: "library", Component: LibraryPage },
      { path: "capture", Component: CapturePage },
      { path: "collections", lazy: lazyPage(() => import("./components/CollectionsPage"), "CollectionsPage") },
      { path: "collections/:id", lazy: lazyPage(() => import("./components/CollectionDetailPage"), "CollectionDetailPage") },
      { path: "c/:slug", lazy: lazyPage(() => import("./components/PublicCollectionPage"), "PublicCollectionPage") },
      { path: "map", lazy: lazyPage(() => import("./components/MapPage"), "MapPage") },
      { path: "search", lazy: lazyPage(() => import("./components/SearchHubPage"), "SearchHubPage") },
      { path: "editions", lazy: lazyPage(() => import("./components/EditionsPage"), "EditionsPage") },
      { path: "editions/:id", lazy: lazyPage(() => import("./components/EditionPage"), "EditionPage") },
      { path: "trails/:slug", lazy: lazyPage(() => import("./components/PublicTrailPage"), "PublicTrailPage") },
      { path: "settings", lazy: lazyPage(() => import("./components/SettingsPage"), "SettingsPage") },
      { path: "thinking", lazy: lazyPage(() => import("./components/ThoughtSpacePage"), "ThoughtSpacePage") },
      { path: "claude", lazy: lazyPage(() => import("./components/ClaudeIntegrationPage"), "ClaudeIntegrationPage") },
      { path: "pricing", lazy: lazyPage(() => import("./components/PricingPage"), "PricingPage") },
      { path: "terms", lazy: lazyPage(() => import("./components/TermsPage"), "TermsPage") },
      { path: "privacy", lazy: lazyPage(() => import("./components/PrivacyPage"), "PrivacyPage") },
      { path: "u/:handle", lazy: lazyPage(() => import("./components/PublicProfilePage"), "PublicProfilePage") },
      { path: "onboarding", Component: OnboardingGate },
      { path: "*", Component: NotFoundPage },
    ],
  },
]);
